import { palette } from '../../styles/tokens';
import { parseMoney } from '../../lib/money';

/**
 * MoneyInput — precision-safe currency input primitive.
 *
 * Emits canonical dollars-as-string ("150.00") via onChange. Raw user
 * keystrokes are reflected locally during typing; canonicalization runs
 * on blur, at which point the canonical string is emitted via onChange
 * and the optional onBlur callback fires.
 *
 * Uses type="text" + inputMode="decimal" instead of type="number" to
 * avoid Chrome's number-field steppers AND to permit pasted values
 * containing commas, $, or whitespace without browser-level validation
 * rejection. The parseMoney helper strips those on canonicalize.
 *
 * Prefix $ sits inside the field via an absolutely positioned span;
 * the input value itself is the bare numeric string.
 *
 * Rule #34 (Session 65): consumers always pass and receive money as
 * dollars-as-string. Never JavaScript numbers.
 */

export interface MoneyInputProps {
  value: string;                      // canonical money string; "150.00" or ""
  onChange: (value: string) => void;  // emits raw user input during typing, canonical on blur
  onBlur?: () => void;                // fires after the primitive has canonicalized + emitted via onChange
  allowNegative?: boolean;            // default false
  placeholder?: string;               // default "0.00"
  disabled?: boolean;
  error?: string;                     // optional error message rendered below
  ariaLabel?: string;
  autoFocus?: boolean;
  id?: string;
  name?: string;
  className?: string;                 // applied to outer wrapper
  style?: React.CSSProperties;        // merged into the wrapper for width sizing etc.
}

const WRAPPER: React.CSSProperties = {
  position: 'relative',
  // minWidth: 0 lets the wrapper shrink inside tight flex columns
  // (e.g., line-item tables). Without it, the inner input's default
  // size=20 intrinsic min-content bleeds up and expands the parent
  // flex item beyond its declared width, squeezing siblings.
  minWidth: 0,
};

const PREFIX: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 13.5,
  color: palette.g400,
  pointerEvents: 'none',
  userSelect: 'none',
};

const INPUT_BASE: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px 9px 24px', // extra left padding for the $ prefix
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
  textAlign: 'right' as const,
};

const INPUT_ERROR: React.CSSProperties = {
  borderColor: palette.red,
};

const INPUT_DISABLED: React.CSSProperties = {
  background: palette.g50,
  color: palette.g400,
  cursor: 'not-allowed',
};

const ERROR_TEXT: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: palette.red,
};

/**
 * Filter a raw keystroke-level string to the character set we accept
 * during typing. Strip anything that isn't a digit, dot, optional leading
 * sign, comma/space/$ (pass-through — parseMoney strips them on blur).
 * We don't hard-reject commas/$ during typing because pasting preserves
 * them and users expect the paste to survive to the blur canonicalize step.
 */
function filterKeystroke(raw: string, allowNegative: boolean): string {
  // Preserve the characters that parseMoney understands; reject letters.
  // Allow one leading minus if allowNegative, else strip minuses.
  let out = raw.replace(/[^\d.\-,$\s]/g, '');
  if (!allowNegative) out = out.replace(/-/g, '');
  return out;
}

export function MoneyInput({
  value,
  onChange,
  onBlur,
  allowNegative = false,
  placeholder = '0.00',
  disabled,
  error,
  ariaLabel,
  autoFocus,
  id,
  name,
  className,
  style,
}: MoneyInputProps) {
  // Pure controlled input. Parent owns the value; MoneyInput filters
  // keystrokes on change and canonicalizes on blur. Parent passes the
  // initial value pre-canonicalized (from DB via canonicalMoney on read).
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = filterKeystroke(e.target.value, allowNegative);
    // Emit raw (filtered) keystroke during typing; canonicalization
    // runs on blur so the user sees exactly what they typed.
    onChange(filtered);
  };

  const handleBlur = () => {
    const canonical = parseMoney(value);
    if (canonical !== value) onChange(canonical);
    onBlur?.();
  };

  const inputStyle: React.CSSProperties = {
    ...INPUT_BASE,
    ...(error ? INPUT_ERROR : {}),
    ...(disabled ? INPUT_DISABLED : {}),
  };

  return (
    <div className={className} style={{ ...WRAPPER, ...style }}>
      <span style={PREFIX}>$</span>
      <input
        type="text"
        inputMode="decimal"
        size={1}
        value={value ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        autoFocus={autoFocus}
        id={id}
        name={name}
        style={inputStyle}
      />
      {error && <div style={ERROR_TEXT}>{error}</div>}
    </div>
  );
}
